/**
 * Animation Preset Definitions
 *
 * Each preset defines coordinated animations for different semantic roles
 * (heading, subheading, body, hero-image, supporting-media, decoration).
 * Presets use the same animation names as the existing per-element system
 * (slide, fade, bounce, zoom, etc.) and produce cssAnimation arrays.
 */

import { AnimationPreset } from "./types";

export const PRESETS: AnimationPreset[] = [
  {
    id: "corporate-slide-in",
    name: "Corporate Slide In",
    icon: "corporate-slide-in",
    rules: [
      {
        role: "heading",
        match: { type: "textbox", sizeRank: "largest" },
        animations: [
          {
            stage: "start",
            name: "slide",
            direction: "left",
            transition: "in",
            delay: 0,
            duration: 1000,
            textType: "textbox"
          }
        ]
      },
      {
        role: "subheading",
        match: { type: "textbox", sizeRank: "second" },
        animations: [
          {
            stage: "start",
            name: "slide",
            direction: "left",
            transition: "in",
            delay: 200,
            duration: 1000,
            textType: "textbox"
          }
        ]
      },
      {
        role: "body",
        match: { type: "textbox", sizeRank: "other" },
        staggerDirection: "ttb-ltr",
        staggerOffset: 150,
        animations: [
          {
            stage: "start",
            name: "fade",
            direction: "",
            transition: "in",
            delay: 400,
            duration: 1000,
            textType: "textbox"
          }
        ]
      },
      {
        role: "hero-image",
        match: { type: ["image", "video"], sizeRank: "largest" },
        animations: [
          {
            stage: "start",
            name: "slide",
            direction: "right",
            transition: "in",
            delay: 100,
            duration: 1000
          }
        ]
      },
      {
        role: "supporting-media",
        match: { type: ["image", "video", "vector"], sizeRank: "other" },
        staggerDirection: "ttb-ltr",
        staggerOffset: 100,
        animations: [
          {
            stage: "start",
            name: "fade",
            direction: "",
            transition: "in",
            delay: 300,
            duration: 1000
          }
        ]
      },
      {
        role: "decoration",
        match: { type: ["vector", "qrcode"], sizeRank: "smallest" },
        staggerDirection: "ttb-ltr",
        staggerOffset: 100,
        animations: [
          {
            stage: "start",
            name: "fade",
            direction: "",
            transition: "in",
            delay: 500,
            duration: 1000
          }
        ]
      }
    ]
  },

  {
    id: "gentle-fade",
    name: "Gentle Fade",
    icon: "gentle-fade",
    rules: [
      {
        role: "heading",
        match: { type: "textbox", sizeRank: "largest" },
        animations: [
          {
            stage: "start",
            name: "fade",
            direction: "",
            transition: "in",
            delay: 0,
            duration: 1000,
            textType: "textbox"
          }
        ]
      },
      {
        role: "subheading",
        match: { type: "textbox", sizeRank: "second" },
        animations: [
          {
            stage: "start",
            name: "fade",
            direction: "",
            transition: "in",
            delay: 300,
            duration: 1000,
            textType: "textbox"
          }
        ]
      },
      {
        role: "body",
        match: { type: "textbox", sizeRank: "other" },
        staggerDirection: "ttb-ltr",
        staggerOffset: 200,
        animations: [
          {
            stage: "start",
            name: "fade",
            direction: "",
            transition: "in",
            delay: 600,
            duration: 1000,
            textType: "textbox"
          }
        ]
      },
      {
        role: "hero-image",
        match: { type: ["image", "video"], sizeRank: "largest" },
        animations: [
          {
            stage: "start",
            name: "fade",
            direction: "",
            transition: "in",
            delay: 100,
            duration: 1000
          }
        ]
      },
      {
        role: "supporting-media",
        match: { type: ["image", "video", "vector"], sizeRank: "other" },
        staggerDirection: "ttb-ltr",
        staggerOffset: 150,
        animations: [
          {
            stage: "start",
            name: "fade",
            direction: "",
            transition: "in",
            delay: 400,
            duration: 1000
          }
        ]
      },
      {
        role: "decoration",
        match: { type: ["vector", "qrcode"], sizeRank: "smallest" },
        staggerDirection: "ttb-ltr",
        staggerOffset: 150,
        animations: [
          {
            stage: "start",
            name: "fade",
            direction: "",
            transition: "in",
            delay: 700,
            duration: 1000
          }
        ]
      }
    ]
  },

  {
    id: "bold-entrance",
    name: "Bold Entrance",
    icon: "bold-entrance",
    rules: [
      {
        role: "heading",
        match: { type: "textbox", sizeRank: "largest" },
        animations: [
          {
            stage: "start",
            name: "zoom",
            direction: "",
            transition: "in",
            delay: 0,
            duration: 1000,
            textType: "textbox"
          },
          {
            stage: "middle",
            name: "tada",
            direction: "",
            transition: "",
            delay: 1200,
            duration: 1000,
            textType: "textbox"
          }
        ]
      },
      {
        role: "subheading",
        match: { type: "textbox", sizeRank: "second" },
        animations: [
          {
            stage: "start",
            name: "bounce",
            direction: "down",
            transition: "in",
            delay: 200,
            duration: 1000,
            textType: "textbox"
          }
        ]
      },
      {
        role: "body",
        match: { type: "textbox", sizeRank: "other" },
        staggerDirection: "ttb-ltr",
        staggerOffset: 150,
        animations: [
          {
            stage: "start",
            name: "fade",
            direction: "",
            transition: "in",
            delay: 500,
            duration: 1000,
            textType: "textbox"
          }
        ]
      },
      {
        role: "hero-image",
        match: { type: ["image", "video"], sizeRank: "largest" },
        animations: [
          {
            stage: "start",
            name: "zoom",
            direction: "",
            transition: "in",
            delay: 0,
            duration: 1000
          }
        ]
      },
      {
        role: "supporting-media",
        match: { type: ["image", "video", "vector"], sizeRank: "other" },
        staggerDirection: "ttb-ltr",
        staggerOffset: 100,
        animations: [
          {
            stage: "start",
            name: "bounce",
            direction: "",
            transition: "in",
            delay: 300,
            duration: 1000
          }
        ]
      },
      {
        role: "decoration",
        match: { type: ["vector", "qrcode"], sizeRank: "smallest" },
        staggerDirection: "random",
        staggerOffset: 100,
        animations: [
          {
            stage: "start",
            name: "zoom",
            direction: "",
            transition: "in",
            delay: 400,
            duration: 1000
          }
        ]
      }
    ]
  },

  {
    id: "quick-reveal",
    name: "Quick Reveal",
    icon: "quick-reveal",
    rules: [
      {
        role: "heading",
        match: { type: "textbox", sizeRank: "largest" },
        animations: [
          {
            stage: "start",
            name: "slide",
            direction: "up",
            transition: "in",
            delay: 0,
            duration: 1000,
            textType: "textbox"
          }
        ]
      },
      {
        role: "subheading",
        match: { type: "textbox", sizeRank: "second" },
        animations: [
          {
            stage: "start",
            name: "slide",
            direction: "up",
            transition: "in",
            delay: 100,
            duration: 1000,
            textType: "textbox"
          }
        ]
      },
      {
        role: "body",
        match: { type: "textbox", sizeRank: "other" },
        staggerDirection: "ttb-ltr",
        staggerOffset: 100,
        animations: [
          {
            stage: "start",
            name: "slide",
            direction: "up",
            transition: "in",
            delay: 200,
            duration: 1000,
            textType: "textbox"
          }
        ]
      },
      {
        role: "hero-image",
        match: { type: ["image", "video"], sizeRank: "largest" },
        animations: [
          {
            stage: "start",
            name: "zoom",
            direction: "",
            transition: "in",
            delay: 0,
            duration: 1000
          }
        ]
      },
      {
        role: "supporting-media",
        match: { type: ["image", "video", "vector"], sizeRank: "other" },
        staggerDirection: "ttb-ltr",
        staggerOffset: 80,
        animations: [
          {
            stage: "start",
            name: "zoom",
            direction: "",
            transition: "in",
            delay: 100,
            duration: 1000
          }
        ]
      },
      {
        role: "decoration",
        match: { type: ["vector", "qrcode"], sizeRank: "smallest" },
        staggerDirection: "ttb-ltr",
        staggerOffset: 80,
        animations: [
          {
            stage: "start",
            name: "fade",
            direction: "",
            transition: "in",
            delay: 200,
            duration: 1000
          }
        ]
      }
    ]
  },

  {
    id: "minimal-pulse",
    name: "Minimal Pulse",
    icon: "minimal-pulse",
    rules: [
      {
        role: "heading",
        match: { type: "textbox", sizeRank: "largest" },
        animations: [
          {
            stage: "start",
            name: "fade",
            direction: "",
            transition: "in",
            delay: 0,
            duration: 1000,
            textType: "textbox"
          },
          {
            stage: "middle",
            name: "pulse",
            direction: "",
            transition: "",
            delay: 1200,
            duration: 1000,
            textType: "textbox"
          }
        ]
      },
      {
        role: "subheading",
        match: { type: "textbox", sizeRank: "second" },
        animations: [
          {
            stage: "start",
            name: "fade",
            direction: "",
            transition: "in",
            delay: 200,
            duration: 1000,
            textType: "textbox"
          }
        ]
      },
      {
        role: "body",
        match: { type: "textbox", sizeRank: "other" },
        staggerDirection: "ttb-ltr",
        staggerOffset: 150,
        animations: [
          {
            stage: "start",
            name: "fade",
            direction: "",
            transition: "in",
            delay: 400,
            duration: 1000,
            textType: "textbox"
          }
        ]
      },
      {
        role: "hero-image",
        match: { type: ["image", "video"], sizeRank: "largest" },
        animations: [
          {
            stage: "start",
            name: "fade",
            direction: "",
            transition: "in",
            delay: 0,
            duration: 1000
          },
          {
            stage: "middle",
            name: "pulse",
            direction: "",
            transition: "",
            delay: 1200,
            duration: 1000
          }
        ]
      },
      {
        role: "supporting-media",
        match: { type: ["image", "video", "vector"], sizeRank: "other" },
        staggerDirection: "ttb-ltr",
        staggerOffset: 100,
        animations: [
          {
            stage: "start",
            name: "fade",
            direction: "",
            transition: "in",
            delay: 300,
            duration: 1000
          }
        ]
      },
      {
        role: "decoration",
        match: { type: ["vector", "qrcode"], sizeRank: "smallest" },
        staggerDirection: "ttb-ltr",
        staggerOffset: 100,
        animations: [
          {
            stage: "start",
            name: "fade",
            direction: "",
            transition: "in",
            delay: 500,
            duration: 1000
          }
        ]
      }
    ]
  },

  {
    id: "pop-and-bounce",
    name: "Pop & Bounce",
    icon: "pop-and-bounce",
    rules: [
      {
        role: "heading",
        match: { type: "textbox", sizeRank: "largest" },
        animations: [
          {
            stage: "start",
            name: "bounce",
            direction: "down",
            transition: "in",
            delay: 0,
            duration: 1000,
            textType: "textbox"
          },
          {
            stage: "middle",
            name: "rubberBand",
            direction: "",
            transition: "",
            delay: 1200,
            duration: 1000,
            textType: "textbox"
          }
        ]
      },
      {
        role: "subheading",
        match: { type: "textbox", sizeRank: "second" },
        animations: [
          {
            stage: "start",
            name: "bounce",
            direction: "up",
            transition: "in",
            delay: 200,
            duration: 1000,
            textType: "textbox"
          }
        ]
      },
      {
        role: "body",
        match: { type: "textbox", sizeRank: "other" },
        staggerDirection: "ttb-ltr",
        staggerOffset: 120,
        animations: [
          {
            stage: "start",
            name: "zoom",
            direction: "",
            transition: "in",
            delay: 400,
            duration: 1000,
            textType: "textbox"
          }
        ]
      },
      {
        role: "hero-image",
        match: { type: ["image", "video"], sizeRank: "largest" },
        animations: [
          {
            stage: "start",
            name: "zoom",
            direction: "",
            transition: "in",
            delay: 0,
            duration: 1000
          },
          {
            stage: "middle",
            name: "pulse",
            direction: "",
            transition: "",
            delay: 1200,
            duration: 1000
          }
        ]
      },
      {
        role: "supporting-media",
        match: { type: ["image", "video", "vector"], sizeRank: "other" },
        staggerDirection: "random",
        staggerOffset: 100,
        animations: [
          {
            stage: "start",
            name: "bounce",
            direction: "",
            transition: "in",
            delay: 200,
            duration: 1000
          }
        ]
      },
      {
        role: "decoration",
        match: { type: ["vector", "qrcode"], sizeRank: "smallest" },
        staggerDirection: "random",
        staggerOffset: 80,
        animations: [
          {
            stage: "start",
            name: "zoom",
            direction: "",
            transition: "in",
            delay: 300,
            duration: 1000
          }
        ]
      }
    ]
  },

  {
    id: "typewriter",
    name: "Typewriter",
    icon: "typewriter",
    rules: [
      {
        role: "heading",
        match: { type: "textbox", sizeRank: "largest" },
        animations: [
          {
            stage: "start",
            name: "fade",
            direction: "",
            transition: "in",
            delay: 0,
            duration: 1000,
            textType: "letter"
          }
        ]
      },
      {
        role: "subheading",
        match: { type: "textbox", sizeRank: "second" },
        animations: [
          {
            stage: "start",
            name: "fade",
            direction: "",
            transition: "in",
            delay: 400,
            duration: 1000,
            textType: "letter"
          }
        ]
      },
      {
        role: "body",
        match: { type: "textbox", sizeRank: "other" },
        staggerDirection: "ttb-ltr",
        staggerOffset: 300,
        animations: [
          {
            stage: "start",
            name: "fade",
            direction: "",
            transition: "in",
            delay: 800,
            duration: 1000,
            textType: "word"
          }
        ]
      },
      {
        role: "hero-image",
        match: { type: ["image", "video"], sizeRank: "largest" },
        animations: [
          {
            stage: "start",
            name: "fade",
            direction: "",
            transition: "in",
            delay: 200,
            duration: 1000
          }
        ]
      },
      {
        role: "supporting-media",
        match: { type: ["image", "video", "vector"], sizeRank: "other" },
        staggerDirection: "ttb-ltr",
        staggerOffset: 200,
        animations: [
          {
            stage: "start",
            name: "fade",
            direction: "",
            transition: "in",
            delay: 600,
            duration: 1000
          }
        ]
      },
      {
        role: "decoration",
        match: { type: ["vector", "qrcode"], sizeRank: "smallest" },
        staggerDirection: "ttb-ltr",
        staggerOffset: 150,
        animations: [
          {
            stage: "start",
            name: "fade",
            direction: "",
            transition: "in",
            delay: 1000,
            duration: 1000
          }
        ]
      }
    ]
  },

  {
    id: "cinematic",
    name: "Cinematic",
    icon: "cinematic",
    rules: [
      {
        role: "heading",
        match: { type: "textbox", sizeRank: "largest" },
        animations: [
          {
            stage: "start",
            name: "fade",
            direction: "",
            transition: "in",
            delay: 500,
            duration: 1000,
            textType: "word"
          }
        ]
      },
      {
        role: "subheading",
        match: { type: "textbox", sizeRank: "second" },
        animations: [
          {
            stage: "start",
            name: "fade",
            direction: "",
            transition: "in",
            delay: 800,
            duration: 1000,
            textType: "word"
          }
        ]
      },
      {
        role: "body",
        match: { type: "textbox", sizeRank: "other" },
        staggerDirection: "ttb-ltr",
        staggerOffset: 200,
        animations: [
          {
            stage: "start",
            name: "fade",
            direction: "",
            transition: "in",
            delay: 1100,
            duration: 1000,
            textType: "textbox"
          }
        ]
      },
      {
        role: "hero-image",
        match: { type: ["image", "video"], sizeRank: "largest" },
        animations: [
          {
            stage: "start",
            name: "zoom",
            direction: "",
            transition: "in",
            delay: 0,
            duration: 1000
          }
        ]
      },
      {
        role: "supporting-media",
        match: { type: ["image", "video", "vector"], sizeRank: "other" },
        staggerDirection: "ttb-ltr",
        staggerOffset: 200,
        animations: [
          {
            stage: "start",
            name: "fade",
            direction: "",
            transition: "in",
            delay: 300,
            duration: 1000
          }
        ]
      },
      {
        role: "decoration",
        match: { type: ["vector", "qrcode"], sizeRank: "smallest" },
        staggerDirection: "center-out",
        staggerOffset: 150,
        animations: [
          {
            stage: "start",
            name: "fade",
            direction: "",
            transition: "in",
            delay: 600,
            duration: 1000
          }
        ]
      }
    ]
  },

  {
    id: "elegant-drift",
    name: "Elegant Drift",
    icon: "elegant-drift",
    rules: [
      {
        role: "heading",
        match: { type: "textbox", sizeRank: "largest" },
        animations: [
          {
            stage: "start",
            name: "back",
            direction: "left",
            transition: "in",
            delay: 0,
            duration: 1000,
            textType: "textbox"
          }
        ]
      },
      {
        role: "subheading",
        match: { type: "textbox", sizeRank: "second" },
        animations: [
          {
            stage: "start",
            name: "back",
            direction: "right",
            transition: "in",
            delay: 200,
            duration: 1000,
            textType: "textbox"
          }
        ]
      },
      {
        role: "body",
        match: { type: "textbox", sizeRank: "other" },
        staggerDirection: "ttb-ltr",
        staggerOffset: 150,
        animations: [
          {
            stage: "start",
            name: "fade",
            direction: "",
            transition: "in",
            delay: 500,
            duration: 1000,
            textType: "textbox"
          }
        ]
      },
      {
        role: "hero-image",
        match: { type: ["image", "video"], sizeRank: "largest" },
        animations: [
          {
            stage: "start",
            name: "back",
            direction: "up",
            transition: "in",
            delay: 100,
            duration: 1000
          }
        ]
      },
      {
        role: "supporting-media",
        match: { type: ["image", "video", "vector"], sizeRank: "other" },
        staggerDirection: "ttb-ltr",
        staggerOffset: 120,
        animations: [
          {
            stage: "start",
            name: "back",
            direction: "down",
            transition: "in",
            delay: 300,
            duration: 1000
          }
        ]
      },
      {
        role: "decoration",
        match: { type: ["vector", "qrcode"], sizeRank: "smallest" },
        staggerDirection: "center-out",
        staggerOffset: 100,
        animations: [
          {
            stage: "start",
            name: "fade",
            direction: "",
            transition: "in",
            delay: 600,
            duration: 1000
          }
        ]
      }
    ]
  },

  {
    id: "cascade",
    name: "Cascade",
    icon: "cascade",
    rules: [
      {
        role: "heading",
        match: { type: "textbox", sizeRank: "largest" },
        animations: [
          {
            stage: "start",
            name: "slide",
            direction: "down",
            transition: "in",
            delay: 0,
            duration: 1000,
            textType: "textbox"
          }
        ]
      },
      {
        role: "subheading",
        match: { type: "textbox", sizeRank: "second" },
        animations: [
          {
            stage: "start",
            name: "slide",
            direction: "down",
            transition: "in",
            delay: 150,
            duration: 1000,
            textType: "textbox"
          }
        ]
      },
      {
        role: "body",
        match: { type: "textbox", sizeRank: "other" },
        staggerDirection: "ttb-ltr",
        staggerOffset: 100,
        animations: [
          {
            stage: "start",
            name: "slide",
            direction: "down",
            transition: "in",
            delay: 300,
            duration: 1000,
            textType: "textbox"
          }
        ]
      },
      {
        role: "hero-image",
        match: { type: ["image", "video"], sizeRank: "largest" },
        animations: [
          {
            stage: "start",
            name: "slide",
            direction: "down",
            transition: "in",
            delay: 50,
            duration: 1000
          }
        ]
      },
      {
        role: "supporting-media",
        match: { type: ["image", "video", "vector"], sizeRank: "other" },
        staggerDirection: "ttb-ltr",
        staggerOffset: 100,
        animations: [
          {
            stage: "start",
            name: "slide",
            direction: "down",
            transition: "in",
            delay: 200,
            duration: 1000
          }
        ]
      },
      {
        role: "decoration",
        match: { type: ["vector", "qrcode"], sizeRank: "smallest" },
        staggerDirection: "ttb-ltr",
        staggerOffset: 80,
        animations: [
          {
            stage: "start",
            name: "fade",
            direction: "",
            transition: "in",
            delay: 400,
            duration: 1000
          }
        ]
      }
    ]
  },

  {
    id: "storyboard",
    name: "Storyboard",
    icon: "storyboard",
    rules: [
      {
        role: "heading",
        match: { type: "textbox", sizeRank: "largest" },
        animations: [
          {
            stage: "start",
            name: "slide",
            direction: "right",
            transition: "in",
            delay: 300,
            duration: 1000,
            textType: "textbox"
          }
        ]
      },
      {
        role: "subheading",
        match: { type: "textbox", sizeRank: "second" },
        animations: [
          {
            stage: "start",
            name: "slide",
            direction: "left",
            transition: "in",
            delay: 600,
            duration: 1000,
            textType: "textbox"
          }
        ]
      },
      {
        role: "body",
        match: { type: "textbox", sizeRank: "other" },
        staggerDirection: "ttb-ltr",
        staggerOffset: 200,
        animations: [
          {
            stage: "start",
            name: "fade",
            direction: "",
            transition: "in",
            delay: 900,
            duration: 1000,
            textType: "textbox"
          }
        ]
      },
      {
        role: "hero-image",
        match: { type: ["image", "video"], sizeRank: "largest" },
        animations: [
          {
            stage: "start",
            name: "zoom",
            direction: "",
            transition: "in",
            delay: 0,
            duration: 1000
          }
        ]
      },
      {
        role: "supporting-media",
        match: { type: ["image", "video", "vector"], sizeRank: "other" },
        staggerDirection: "ttb-ltr",
        staggerOffset: 200,
        animations: [
          {
            stage: "start",
            name: "slide",
            direction: "up",
            transition: "in",
            delay: 400,
            duration: 1000
          }
        ]
      },
      {
        role: "decoration",
        match: { type: ["vector", "qrcode"], sizeRank: "smallest" },
        staggerDirection: "ttb-ltr",
        staggerOffset: 150,
        animations: [
          {
            stage: "start",
            name: "fade",
            direction: "",
            transition: "in",
            delay: 700,
            duration: 1000
          }
        ]
      }
    ]
  },

  {
    id: "flip-and-rotate",
    name: "Flip & Rotate",
    icon: "flip-and-rotate",
    rules: [
      {
        role: "heading",
        match: { type: "textbox", sizeRank: "largest" },
        animations: [
          {
            stage: "start",
            name: "flip",
            direction: "horizontal",
            transition: "in",
            delay: 0,
            duration: 1000,
            textType: "textbox"
          }
        ]
      },
      {
        role: "subheading",
        match: { type: "textbox", sizeRank: "second" },
        animations: [
          {
            stage: "start",
            name: "flip",
            direction: "vertical",
            transition: "in",
            delay: 200,
            duration: 1000,
            textType: "textbox"
          }
        ]
      },
      {
        role: "body",
        match: { type: "textbox", sizeRank: "other" },
        staggerDirection: "ttb-ltr",
        staggerOffset: 120,
        animations: [
          {
            stage: "start",
            name: "fade",
            direction: "",
            transition: "in",
            delay: 500,
            duration: 1000,
            textType: "textbox"
          }
        ]
      },
      {
        role: "hero-image",
        match: { type: ["image", "video"], sizeRank: "largest" },
        animations: [
          {
            stage: "start",
            name: "rotate",
            direction: "left",
            transition: "in",
            delay: 0,
            duration: 1000
          }
        ]
      },
      {
        role: "supporting-media",
        match: { type: ["image", "video", "vector"], sizeRank: "other" },
        staggerDirection: "random",
        staggerOffset: 100,
        animations: [
          {
            stage: "start",
            name: "flip",
            direction: "horizontal",
            transition: "in",
            delay: 200,
            duration: 1000
          }
        ]
      },
      {
        role: "decoration",
        match: { type: ["vector", "qrcode"], sizeRank: "smallest" },
        staggerDirection: "random",
        staggerOffset: 80,
        animations: [
          {
            stage: "start",
            name: "rotate",
            direction: "right",
            transition: "in",
            delay: 400,
            duration: 1000
          }
        ]
      }
    ]
  }
];

/** Get a preset by its ID */
export const getPresetById = (id: string): AnimationPreset | undefined =>
  PRESETS.find(preset => preset.id === id);
